import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  Tooltip,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CompletedIcon,
  HourglassEmpty as PendingIcon,
  Visibility as ViewIcon,
  LocationOn as LocationIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useCompany } from '../contexts/CompanyContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auditAPI, analyticsAPI, aiBuilderAPI, companyAPI, userAPI, AuditSession, AuditAnalysis, AuditSeries, ActionItem, CompanyUser, CompanySite, User } from '../services/api';
import HorizontalBarChart from '../components/Charts/HorizontalBarChart';
import RadarChart, { RadarChartData } from '../components/Charts/RadarChart';
import AIInsightsGenerator from '../components/AIInsightsGenerator';
import ActionItemsManagement from '../components/ActionItemsManagement';

const AuditResultsByCompany: React.FC = () => {
  const { user } = useAuth();
  const { companies, selectedCompany, setSelectedCompany, userPermissions } = useCompany();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [auditSessions, setAuditSessions] = useState<AuditSession[]>([]);
  const [auditAnalyses, setAuditAnalyses] = useState<Record<number, AuditAnalysis>>({});
  const [auditSeries, setAuditSeries] = useState<Record<number, string>>({});
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [actionItemsStats, setActionItemsStats] = useState<{
    total_count: number;
    by_status: { pending: number; in_progress: number; completed: number; cancelled: number; };
    completion_summary?: {
      total_action_items: number;
      completed_action_items: number;
      in_progress_action_items: number;
      total_subtasks: number;
      completed_subtasks: number;
      in_progress_subtasks: number;
      completion_percentage: number;
    } | null;
  } | null>(null);
  const [auditSeriesData, setAuditSeriesData] = useState<Record<number, AuditSeries>>({});
  const [companyAnalytics, setCompanyAnalytics] = useState<any>(null);
  const [isPrimaryUser, setIsPrimaryUser] = useState(false);
  const [assignableUsers, setAssignableUsers] = useState<User[]>([]);
  
  // Site filtering state
  const [companySites, setCompanySites] = useState<CompanySite[]>([]);
  const [selectedSiteFilter, setSelectedSiteFilter] = useState<number | 'all'>('all');
  const [sitesLoading, setSitesLoading] = useState(false);

  // Selected review type for radar chart drill-down
  const [selectedAuditType, setSelectedAuditType] = useState<string | null>(null);
  const [selectedAnalysis, setSelectedAnalysis] = useState<AuditAnalysis | null>(null);

  // Check if this is an action items filtered view
  const isActionItemsView = searchParams.get('filter') === 'action-items';
  
  // Check if this is from analysis & reports view
  const isAnalysisView = searchParams.get('view') === 'analysis';

  // Check for company parameter in URL and set selected company
  useEffect(() => {
    const companyParam = searchParams.get('company');
    if (companyParam && companies.length > 0) {
      const companyId = parseInt(companyParam);
      const company = companies.find(c => c.id === companyId);
      if (company && (!selectedCompany || selectedCompany.id !== companyId)) {
        console.log('✅ Auto-selecting company from URL:', company.name);
        setSelectedCompany(company);
      }
    }
  }, [searchParams, companies, selectedCompany, setSelectedCompany]);

  useEffect(() => {
    if (selectedCompany) {
      // For action items view, only load what's needed (sessions for site filtering)
      if (isActionItemsView) {
        loadActionItemsPageData();
      } else {
        // For other views, load full review data including analyses
        loadAuditData();
      }
      // Sites are now loaded within each data loading function
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany, isAnalysisView, isActionItemsView]);

  /**
   * Completed analyses for a review type, newest first (respects site filter).
   */
  const getMatchingAnalysesForType = (
    auditType: string
  ): Array<{ analysis: AuditAnalysis; session: AuditSession }> => {
    return Object.values(auditAnalyses)
      .map((analysis) => {
        const session = getFilteredSessions().find((s) => s.id === analysis.audit_header);
        if (!session?.end_date) return null;

        const name = session.audit_series
          ? (auditSeries[session.audit_series] || `Review ${session.audit_series}`)
          : 'Unknown Review Type';
        if (name !== auditType) return null;

        return { analysis, session };
      })
      .filter(
        (item): item is { analysis: AuditAnalysis; session: AuditSession } => item !== null
      )
      .sort(
        (a, b) =>
          new Date(b.session.end_date!).getTime() - new Date(a.session.end_date!).getTime()
      );
  };

  // Update selected analysis when selected review type changes (always latest)
  useEffect(() => {
    if (selectedAuditType && Object.keys(auditAnalyses).length > 0) {
      const matches = getMatchingAnalysesForType(selectedAuditType);
      setSelectedAnalysis(matches[0]?.analysis || null);
    } else {
      setSelectedAnalysis(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAuditType, auditAnalyses, auditSessions, auditSeries, selectedSiteFilter]);

  // Get site name by ID
  const getSiteName = (siteId: number): string => {
    const site = companySites.find(s => s.id === siteId);
    return site?.name || `Site ${siteId}`;
  };

  // Filter sessions by selected site
  const getFilteredSessions = (): AuditSession[] => {
    if (selectedSiteFilter === 'all') {
      return auditSessions;
    }
    return auditSessions.filter(session => session.site === selectedSiteFilter);
  };

  const loadAuditData = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setSitesLoading(true);
      
      // Load audit series, sessions, and sites in parallel
      const [auditSeriesArray, sessions, sites] = await Promise.all([
        auditAPI.getAuditSeries(),
        auditAPI.getAuditSessions(selectedCompany.id),
        companyAPI.getCompanySites(selectedCompany.id)
      ]);
      
      // Process audit series for name mapping
      const seriesMap: Record<number, string> = {};
      const seriesDataMap: Record<number, AuditSeries> = {};
      auditSeriesArray.forEach((series: AuditSeries) => {
        seriesMap[series.id] = series.name;
        seriesDataMap[series.id] = series;
      });
      setAuditSeries(seriesMap);
      setAuditSeriesData(seriesDataMap);
      setAuditSessions(sessions);
      setCompanySites(sites);
      setSelectedSiteFilter('all');
      setSitesLoading(false);
      
      // Load analytics and bulk analyses in parallel
      const hasCompletedSessions = sessions.some((s: AuditSession) => s.end_date);
      
      const [analyticsResult, analysesResult] = await Promise.all([
        // Load analytics if in analysis view
        isAnalysisView 
          ? analyticsAPI.getCompanyAnalytics(selectedCompany.id).catch(error => {
              console.error('Error loading company analytics:', error);
              return null;
            })
          : Promise.resolve(null),
        // Load all analyses in one bulk request instead of N individual requests
        hasCompletedSessions
          ? auditAPI.getBulkAuditAnalyses(selectedCompany.id).catch(error => {
              console.error('Error loading bulk analyses:', error);
              return { analyses: {}, count: 0 };
            })
          : Promise.resolve({ analyses: {}, count: 0 })
      ]);
      
      if (analyticsResult) {
        setCompanyAnalytics(analyticsResult);
      }
      
      // Set analyses from bulk response
      setAuditAnalyses(analysesResult.analyses);
      console.log(`✅ Loaded ${analysesResult.count} analyses in single request`);
      
    } catch (error) {
      console.error('Error loading audit data:', error);
      setError('Failed to load review data');
    } finally {
      setLoading(false);
    }
  };

  // Lightweight data load for Action Items page - only loads sessions, sites, and action items
  const loadActionItemsPageData = async () => {
    if (!selectedCompany) return;
    
    try {
      setLoading(true);
      setSitesLoading(true);
      
      // Load all needed data in parallel - single batch of API calls
      // Skip audit series, analyses, and analytics - not needed for action items page
      const [sessions, data, companyUsers, users, sites] = await Promise.all([
        auditAPI.getAuditSessions(selectedCompany.id),
        aiBuilderAPI.getActionItems(selectedCompany.id),
        companyAPI.getCompanyUsers(selectedCompany.id),
        userAPI.getAssignableUsers(selectedCompany.id),
        companyAPI.getCompanySites(selectedCompany.id)
      ]);
      
      setAuditSessions(sessions);
      setActionItems(data.action_items);
      setActionItemsStats({
        total_count: data.total_count,
        by_status: data.by_status,
        completion_summary: data.completion_summary || null
      });
      setAssignableUsers(users);
      setCompanySites(sites);
      setSelectedSiteFilter('all');
      
      // Check if current user is a primary user for this company OR a consultant admin
      const currentUserInCompany = companyUsers.find(
        (u: CompanyUser) => u.user_email === user?.email
      );
      const isClientAdmin = currentUserInCompany?.is_primary_user || false;
      const isConsultantAdmin = userPermissions?.can_manage_clients || false;
      const isSuperAdmin = user?.is_superuser || false;
      const canDeleteActionItems = isClientAdmin || isConsultantAdmin || isSuperAdmin;
      setIsPrimaryUser(canDeleteActionItems);
      console.log('🔐 Delete permissions check:', { isClientAdmin, isConsultantAdmin, isSuperAdmin, canDelete: canDeleteActionItems, userEmail: user?.email });
      console.log('✅ Action items page loaded (all data in parallel)');
    } catch (error) {
      console.error('Error loading action items page:', error);
      setError('Failed to load action items');
    } finally {
      setLoading(false);
      setSitesLoading(false);
    }
  };

  // Lightweight refresh for action items only - doesn't reload users (already cached)
  const loadActionItems = async () => {
    if (!selectedCompany) return;
    
    try {
      // Only fetch action items - users are already loaded and passed via props
      const data = await aiBuilderAPI.getActionItems(selectedCompany.id);
      
      setActionItems(data.action_items);
      setActionItemsStats({
        total_count: data.total_count,
        by_status: data.by_status,
        completion_summary: data.completion_summary || null
      });
      console.log('🔄 Action items refreshed');
    } catch (error) {
      console.error('Error loading action items:', error);
      setError('Failed to load action items');
    }
  };

  const handleCompanyChange = (event: any) => {
    const companyId = event.target.value;
    const company = companies.find(c => c.id === companyId);
    if (company) {
      setSelectedCompany(company);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusChip = (session: AuditSession) => {
    if (session.end_date) {
      return (
        <Chip
          icon={<CompletedIcon />}
          label="Completed"
          color="success"
          size="small"
        />
      );
    } else {
      return (
        <Chip
          icon={<PendingIcon />}
          label="In Progress"
          color="warning"
          size="small"
        />
      );
    }
  };

  const getScoreChip = (score?: number) => {
    if (score === undefined) return null;
    
    let color: 'error' | 'warning' | 'success' = 'warning';
    if (score >= 80) color = 'success';
    else if (score < 60) color = 'error';
    
    return (
      <Chip
        label={`${score.toFixed(1)}%`}
        color={color}
        size="small"
        variant="outlined"
      />
    );
  };

  // Use filtered sessions for all calculations
  const filteredSessions = getFilteredSessions();
  const completedAudits = filteredSessions.filter(session => session.end_date);
  const inProgressAudits = filteredSessions.filter(session => !session.end_date);
  const averageScore = completedAudits.length > 0 
    ? completedAudits.reduce((sum, session) => {
        const analysis = auditAnalyses[session.id];
        return sum + (analysis?.overall_score || 0);
      }, 0) / completedAudits.length 
    : 0;

  // Filter action items by selected site
  const filteredActionItems = useMemo(() => {
    if (selectedSiteFilter === 'all') {
      return actionItems;
    }
    // Get session IDs for the selected site
    const siteSessionIds = new Set(
      auditSessions
        .filter(session => session.site === selectedSiteFilter)
        .map(session => session.id)
    );
    // Filter action items to only those belonging to sessions at the selected site
    return actionItems.filter(item => siteSessionIds.has(item.audit_session_id));
  }, [actionItems, auditSessions, selectedSiteFilter]);

  // Calculate completion summary for filtered action items
  const filteredCompletionSummary = useMemo(() => {
    if (filteredActionItems.length === 0) {
      return null;
    }
    
    const totalActionItems = filteredActionItems.length;
    const completedActionItems = filteredActionItems.filter(item => item.status === 'completed').length;
    const inProgressActionItems = filteredActionItems.filter(item => item.status === 'in_progress').length;
    
    // Calculate subtask stats
    let totalSubtasks = 0;
    let completedSubtasks = 0;
    let inProgressSubtasks = 0;
    
    filteredActionItems.forEach(item => {
      if (item.subtasks && item.subtasks.length > 0) {
        totalSubtasks += item.subtasks.length;
        completedSubtasks += item.subtasks.filter(st => st.status === 'completed').length;
        inProgressSubtasks += item.subtasks.filter(st => st.status === 'in_progress').length;
      }
    });
    
    // Calculate completion percentage based on subtasks if available, otherwise action items
    const completionPercentage = totalSubtasks > 0
      ? Math.round((completedSubtasks / totalSubtasks) * 100)
      : totalActionItems > 0
        ? Math.round((completedActionItems / totalActionItems) * 100)
        : 0;
    
    return {
      session_id: 0,
      company_id: selectedCompany?.id || 0,
      audit_name: '',
      site_name: '',
      total_action_items: totalActionItems,
      completed_action_items: completedActionItems,
      in_progress_action_items: inProgressActionItems,
      total_subtasks: totalSubtasks,
      completed_subtasks: completedSubtasks,
      in_progress_subtasks: inProgressSubtasks,
      completion_percentage: completionPercentage,
    };
  }, [filteredActionItems, selectedCompany]);

  // Handle site filter change
  const handleSiteFilterChange = (_event: React.MouseEvent<HTMLElement>, newValue: number | 'all' | null) => {
    if (newValue !== null) {
      setSelectedSiteFilter(newValue);
      // Reset selected audit type when site filter changes
      setSelectedAuditType(null);
    }
  };

  // Get horizontal bar chart data (sorted by score)
  const getHorizontalBarData = () => {
    if (!auditAnalyses || Object.keys(auditAnalyses).length === 0) return [];
    if (!auditSeries || Object.keys(auditSeries).length === 0) return [];

    const latestAnalysesByType: Record<string, { analysis: any; session: any }> = {};
    
    Object.values(auditAnalyses).forEach(analysis => {
      const session = filteredSessions.find(s => s.id === analysis.audit_header);
      if (!session) return;
      
      const auditName = session.audit_series 
        ? (auditSeries[session.audit_series] || `Review ${session.audit_series}`)
        : 'Unknown Review Type';
      
      if (!latestAnalysesByType[auditName] || 
          (session.end_date && (!latestAnalysesByType[auditName].session.end_date || 
           new Date(session.end_date) > new Date(latestAnalysesByType[auditName].session.end_date)))) {
        latestAnalysesByType[auditName] = { analysis, session };
      }
    });

    return Object.values(latestAnalysesByType).map(({ analysis, session }) => {
      const auditName = session.audit_series 
        ? (auditSeries[session.audit_series] || `Review ${session.audit_series}`)
        : 'Unknown Review Type';
      
      return {
        auditType: auditName,
        overallScore: analysis.overall_score || 0,
        sessionId: session?.id,
      };
    });
  };

  /** Clean category label for radar axes. */
  const getCategoryLabel = (categoryName?: string): string => {
    return (categoryName || 'Unknown').trim() || 'Unknown';
  };

  /** Format session end date as MM/DD/YYYY for radar legend labels. */
  const formatRadarDate = (endDate: string): string => {
    const d = new Date(endDate);
    return d.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    });
  };

  /**
   * Radar data for a review type: latest review + previous (max 2 lines).
   * Matches the session analysis "Performance Trends Over Time" pattern.
   */
  const getRadarDataForAudit = (auditType: string): RadarChartData | null => {
    if (!auditAnalyses || Object.keys(auditAnalyses).length === 0) return null;

    // Newest first; only the last 2 completed reviews
    const matches = getMatchingAnalysesForType(auditType).slice(0, 2);
    if (matches.length === 0) return null;

    const latest = matches[0];
    const previous = matches[1];
    const latestScores = latest.analysis.category_scores || [];
    if (latestScores.length === 0) return null;

    const labels = latestScores.map((cat) => getCategoryLabel(cat.name));
    const latestData = latestScores.map((cat) => Math.round(cat.score || 0));

    // Same palette as session-level historical_comparison
    const colors = [
      {
        bg: 'rgba(54, 162, 235, 0.2)',
        border: 'rgba(54, 162, 235, 1)',
      },
      {
        bg: 'rgba(255, 99, 132, 0.2)',
        border: 'rgba(255, 99, 132, 1)',
      },
    ];

    const datasets: RadarChartData['datasets'] = [
      {
        label: `Current (${formatRadarDate(latest.session.end_date!)})`,
        data: latestData,
        backgroundColor: colors[0].bg,
        borderColor: colors[0].border,
        borderWidth: 2,
        pointBackgroundColor: colors[0].border,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: colors[0].border,
      },
    ];

    if (previous) {
      const previousByLabel = new Map(
        (previous.analysis.category_scores || []).map((cat) => [
          getCategoryLabel(cat.name),
          Math.round(cat.score || 0),
        ])
      );
      datasets.push({
        label: `Previous (${formatRadarDate(previous.session.end_date!)})`,
        data: labels.map((label) => previousByLabel.get(label) ?? 0),
        backgroundColor: colors[1].bg,
        borderColor: colors[1].border,
        borderWidth: 2,
        pointBackgroundColor: colors[1].border,
        pointBorderColor: '#fff',
        pointHoverBackgroundColor: '#fff',
        pointHoverBorderColor: colors[1].border,
      });
    }

    const siteNote =
      selectedSiteFilter !== 'all'
        ? `Showing trends for ${getSiteName(selectedSiteFilter as number)}`
        : 'Showing results across all sites';
    const comparisonNote =
      matches.length > 1
        ? `${siteNote} · Comparing last 2 reviews`
        : `${siteNote} · First completed review for this type`;

    return {
      labels,
      datasets,
      comparison_note: comparisonNote,
    };
  };

  /** Prior review detailed results for trend chips in the radar drill-down panel. */
  const getPreviousDetailedResults = (auditType: string) => {
    const matches = getMatchingAnalysesForType(auditType);
    return matches[1]?.analysis.detailed_results;
  };

  // Handle audit type selection from horizontal bar chart
  const handleAuditTypeClick = (auditType: string, sessionId?: number) => {
    // Toggle selection - if same audit clicked, deselect
    if (selectedAuditType === auditType) {
      setSelectedAuditType(null);
    } else {
      setSelectedAuditType(auditType);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        <AssessmentIcon fontSize="large" />
        {isActionItemsView ? 'Action Items Management' : 
         isAnalysisView ? 'Analysis & Reports' : 
         'Review Results by Company'}
      </Typography>

      {isActionItemsView && (
        <Alert severity="info" sx={{ mb: 3 }}>
          📋 Viewing action items and next steps from completed reviews. Use this page to track and manage follow-up tasks.
        </Alert>
      )}

      {isAnalysisView && (
        <Alert severity="success" sx={{ mb: 3 }}>
          📊 Viewing review analysis and reports. Review completed review results, metrics, performance insights, and action item progress.
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Company Selection - Only show if no company is pre-selected via URL */}
      {!searchParams.get('company') && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <BusinessIcon color="primary" />
            <Typography variant="h6">
              Select Company
            </Typography>
          </Box>
          
          <FormControl sx={{ minWidth: 300 }}>
            <InputLabel>Company</InputLabel>
            <Select
              value={selectedCompany?.id || ''}
              onChange={handleCompanyChange}
              label="Company"
            >
              {companies.map((company) => (
                <MenuItem key={company.id} value={company.id}>
                  {company.name} - {company.city}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Paper>
      )}

      {selectedCompany && (
        <>
          {/* Site Filter */}
          {companySites.length > 0 && (
            <Paper sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterIcon color="action" />
                  <Typography variant="subtitle1" fontWeight="medium">
                    Filter by Site:
                  </Typography>
                </Box>
                
                <ToggleButtonGroup
                  value={selectedSiteFilter}
                  exclusive
                  onChange={handleSiteFilterChange}
                  size="small"
                  sx={{ flexWrap: 'wrap' }}
                >
                  <ToggleButton value="all" sx={{ px: 2 }}>
                    <Tooltip title="Show all sites">
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        All Sites
                        <Chip 
                          label={auditSessions.length} 
                          size="small" 
                          sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                        />
                      </Box>
                    </Tooltip>
                  </ToggleButton>
                  {companySites.map((site) => {
                    const siteSessionCount = auditSessions.filter(s => s.site === site.id).length;
                    return (
                      <ToggleButton key={site.id} value={site.id} sx={{ px: 2 }}>
                        <Tooltip title={`Filter by ${site.name}`}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <LocationIcon sx={{ fontSize: 16 }} />
                            {site.name}
                            <Chip 
                              label={siteSessionCount} 
                              size="small" 
                              sx={{ ml: 0.5, height: 20, fontSize: '0.7rem' }}
                            />
                          </Box>
                        </Tooltip>
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>

                {selectedSiteFilter !== 'all' && (
                  <Button 
                    size="small" 
                    onClick={() => setSelectedSiteFilter('all')}
                    sx={{ ml: 'auto' }}
                  >
                    Clear Filter
                  </Button>
                )}
              </Box>
              
              {selectedSiteFilter !== 'all' && (
                <Alert severity="info" sx={{ mt: 2 }} icon={<LocationIcon />}>
                  Showing results for <strong>{getSiteName(selectedSiteFilter as number)}</strong> only. 
                  {' '}{filteredSessions.length} review session{filteredSessions.length !== 1 ? 's' : ''} found.
                </Alert>
              )}
            </Paper>
          )}

          {/* Analytics Dashboard for Analysis View */}
          {isAnalysisView && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                📊 {selectedSiteFilter !== 'all' ? `${getSiteName(selectedSiteFilter as number)} Performance Analytics` : 'Company Performance Analytics'}
              </Typography>
              
              {/* Key Performance Metrics - calculated from filtered data */}
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, mb: 3 }}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="primary.main" gutterBottom>
                      Health Score
                    </Typography>
                    <Typography variant="h3" color="primary.main">
                      {averageScore > 0 ? `${averageScore.toFixed(0)}%` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedSiteFilter !== 'all' ? `${getSiteName(selectedSiteFilter as number)} review health` : 'Overall company review health'}
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="error.main" gutterBottom>
                      Overdue Reviews
                    </Typography>
                    <Typography variant="h3" color="error.main">
                      {companyAnalytics?.overdue_audits || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Reviews past due date
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="info.main" gutterBottom>
                      In Progress
                    </Typography>
                    <Typography variant="h3" color="info.main">
                      {inProgressAudits.length}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Active review sessions
                    </Typography>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent>
                    <Typography variant="h6" color="warning.main" gutterBottom>
                      Open Tasks
                    </Typography>
                    <Typography variant="h3" color="warning.main">
                      {companyAnalytics?.open_tasks || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Pending action items
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {/* Recent Activity Timeline - filtered by site */}
              {completedAudits.length > 0 && (
                <Card sx={{ mb: 3 }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      📋 Recent Activity {selectedSiteFilter !== 'all' && `- ${getSiteName(selectedSiteFilter as number)}`}
                    </Typography>
                    <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
                      {completedAudits.slice(0, 5).map((session, index) => (
                        <Box key={session.id} sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderBottom: index < Math.min(completedAudits.length, 5) - 1 ? '1px solid' : 'none', borderColor: 'divider' }}>
                          <Box sx={{ 
                            width: 40, 
                            height: 40, 
                            borderRadius: '50%', 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            bgcolor: 'success.light'
                          }}>
                            ✅
                          </Box>
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="subtitle2">
                              {session.audit_series_name || auditSeries[session.audit_series] || 'Review'} Completed
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {session.site_name || getSiteName(session.site)} • Score: {auditAnalyses[session.id]?.overall_score?.toFixed(1) || 'N/A'}%
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {session.end_date ? new Date(session.end_date).toLocaleDateString() : ''}
                            </Typography>
                          </Box>
                        </Box>
                      ))}
                      {completedAudits.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                          No completed reviews {selectedSiteFilter !== 'all' ? `for ${getSiteName(selectedSiteFilter as number)}` : ''} yet.
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}

              {/* Enhanced Analytics - only show when we have filtered audit analyses */}
              {/* Only show chart if we have filtered completed audits with analyses */}
              {getHorizontalBarData().length > 0 && (
                <>
                  {/* Audit Performance Overview - Horizontal Bar Chart */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        📊 Review Performance Overview {selectedSiteFilter !== 'all' && `- ${getSiteName(selectedSiteFilter as number)}`}
                      </Typography>
                      <HorizontalBarChart 
                        data={getHorizontalBarData()}
                        selectedAudit={selectedAuditType}
                        onBarClick={handleAuditTypeClick}
                      />
                    </CardContent>
                  </Card>

                  {/* Radar Chart - Shows when an audit type is selected */}
                  {selectedAuditType && getRadarDataForAudit(selectedAuditType) && (
                    <Card sx={{ mb: 3, border: '2px solid #3b82f6' }}>
                      <CardContent sx={{ pb: '12px !important' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            🎯 Category Breakdown: {selectedAuditType}
                          </Typography>
                          <Chip 
                            label="Click bar above to change" 
                            size="small" 
                            variant="outlined"
                            onDelete={() => setSelectedAuditType(null)}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Box>
                        <RadarChart 
                          data={getRadarDataForAudit(selectedAuditType)!}
                          title=""
                          detailedResults={selectedAnalysis?.detailed_results}
                          priorYearDetailedResults={getPreviousDetailedResults(selectedAuditType)}
                        />
                      </CardContent>
                    </Card>
                  )}

                  {/* AI-Generated Insights - pass filtered sessions and site */}
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        🤖 AI Insights & Commentary {selectedSiteFilter !== 'all' && `- ${getSiteName(selectedSiteFilter as number)}`}
                      </Typography>
                      <AIInsightsGenerator 
                        auditAnalyses={auditAnalyses}
                        auditSessions={filteredSessions}
                        auditSeries={auditSeries}
                        auditSeriesData={auditSeriesData}
                        companyName={selectedSiteFilter !== 'all' ? `${selectedCompany?.name} - ${getSiteName(selectedSiteFilter as number)}` : selectedCompany?.name || 'Company'}
                        companyId={selectedCompany?.id || 0}
                        siteId={selectedSiteFilter !== 'all' ? selectedSiteFilter as number : undefined}
                        siteName={selectedSiteFilter !== 'all' ? getSiteName(selectedSiteFilter as number) : undefined}
                      />
                    </CardContent>
                  </Card>
                </>
              )}
            </Box>
          )}

          {/* Summary Cards */}
          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap' }}>
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <AssessmentIcon color="primary" fontSize="large" />
                  <Box>
                    <Typography variant="h4">{filteredSessions.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Total Reviews
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CompletedIcon color="success" fontSize="large" />
                  <Box>
                    <Typography variant="h4">{completedAudits.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Completed
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <PendingIcon color="warning" fontSize="large" />
                  <Box>
                    <Typography variant="h4">{inProgressAudits.length}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      In Progress
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
            <Card sx={{ minWidth: 200, flex: 1 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <ScheduleIcon color="info" fontSize="large" />
                  <Box>
                    <Typography variant="h4">
                      {averageScore > 0 ? `${averageScore.toFixed(1)}%` : 'N/A'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Score
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Action Items Management View */}
          {isActionItemsView && (
            <ActionItemsManagement 
              companyId={selectedCompany.id}
              companyName={selectedCompany.name} 
              actionItems={filteredActionItems}
              onActionItemUpdate={loadActionItems}
              completionSummary={filteredCompletionSummary || undefined}
              isPrimaryUser={isPrimaryUser}
              assignableUsers={assignableUsers}
            />
          )}

          {/* Review Sessions Table - Hidden in Analysis View */}
          {!isAnalysisView && !isActionItemsView && (
            <Paper sx={{ mb: 3 }}>
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>
                Review Sessions for {selectedCompany.name}
              </Typography>
            </Box>
            
            {loading ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Loading review data...
                </Typography>
              </Box>
            ) : filteredSessions.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  {selectedSiteFilter !== 'all' 
                    ? `No review sessions found for ${getSiteName(selectedSiteFilter as number)}.`
                    : 'No review sessions found for this company.'}
                </Typography>
                <Button 
                  variant="contained" 
                  sx={{ mt: 2 }}
                  onClick={() => navigate('/audit-selection')}
                >
                  Start New Review
                </Button>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Review Type</TableCell>
                      <TableCell>Site</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredSessions.map((session) => {
                      const analysis = auditAnalyses[session.id];
                      const auditName = session.audit_series 
                        ? (auditSeries[session.audit_series] || `Review ${session.audit_series}`)
                        : 'Unknown Review Type';
                      return (
                        <TableRow key={session.id} hover>
                          <TableCell>
                            <Typography variant="subtitle2">
                              {session.audit_series_name || auditName}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <LocationIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {session.site_name || getSiteName(session.site)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            {formatDate(session.start_date)}
                          </TableCell>
                          <TableCell>
                            {getStatusChip(session)}
                          </TableCell>
                          <TableCell>
                            {getScoreChip(analysis?.overall_score)}
                          </TableCell>
                          <TableCell>
                            {session.end_date ? (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ViewIcon />}
                                onClick={() => navigate(`/audit-analysis/${session.id}`)}
                              >
                                View Results
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => navigate(`/audit-session/${session.id}`)}
                              >
                                Continue Review
                              </Button>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
          )}
        </>
      )}
    </Container>
  );
};

export default AuditResultsByCompany; 